import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, refreshCurrentUser } from '@/features/profile/hooks/useCurrentUser'
import paymentService from '../services/paymentService'
import { PAYMENT_CONSTANTS } from '../utils/constants'
import {
  trackCheckoutStarted,
  trackPaymentSuccess,
  trackPaymentAbandoned,
} from '@/shared/utils/analytics'
import {
  fbTrackInitiateCheckout,
  fbTrackPurchase,
} from '@/shared/utils/facebookPixel'
import type {
  PricingResponse,
  RazorpayOptions,
  RazorpayResponse,
  RazorpayInstance,
  InitiatePaymentRequest,
  RazorpayCallbackRequest,
  ApplyPromoCodeResponse,
} from '../types'

// Query keys
export const paymentKeys = {
  all: ['payment'] as const,
  pricing: () => [...paymentKeys.all, 'pricing'] as const,
  subscription: (userId: string) => [...paymentKeys.all, 'subscription', userId] as const,
}

// Declare Razorpay on window
declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance
  }
}

export type PaymentStatus = 'idle' | 'creating_order' | 'checkout' | 'verifying' | 'success' | 'error'

interface UsePaymentResult {
  // Pricing data
  pricing: PricingResponse | undefined
  isPricingLoading: boolean
  pricingError: Error | null

  // Payment state
  paymentStatus: PaymentStatus
  paymentError: string | null

  // Promo code state
  promoCode: string
  promoResult: ApplyPromoCodeResponse | null
  isApplyingPromo: boolean
  promoError: string | null

  // Actions
  initiatePayment: () => Promise<void>
  resetPayment: () => void
  applyPromoCode: (code: string) => Promise<void>
  clearPromoCode: () => void
}

/**
 * Hook to fetch pricing information
 */
export function usePricing() {
  return useQuery({
    queryKey: paymentKeys.pricing(),
    queryFn: async () => {
      const response = await paymentService.getPricing()
      return response.data // Extract data from wrapper
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to fetch current subscription
 */
export function useCurrentSubscription(userId: string | undefined) {
  return useQuery({
    queryKey: paymentKeys.subscription(userId || ''),
    queryFn: async () => {
      if (!userId) throw new Error('User ID required')
      const response = await paymentService.getCurrentSubscription(userId)
      return response.data // Extract data from wrapper
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to manage the complete payment flow
 * - Creates order
 * - Opens Razorpay checkout
 * - Verifies payment
 * - Refreshes user profile
 */
export function usePayment(): UsePaymentResult {
  const queryClient = useQueryClient()
  const { user: userProfile } = useCurrentUser()
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle')
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [promoCode, setPromoCode] = useState('')
  const [promoResult, setPromoResult] = useState<ApplyPromoCodeResponse | null>(null)
  const [isApplyingPromo, setIsApplyingPromo] = useState(false)
  const [promoError, setPromoError] = useState<string | null>(null)

  // Fetch pricing
  const {
    data: pricingData,
    isLoading: isPricingLoading,
    error: pricingError,
  } = usePricing()

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (request: InitiatePaymentRequest) => paymentService.createOrder(request),
  })

  // Verify payment mutation
  const verifyPaymentMutation = useMutation({
    mutationFn: (request: RazorpayCallbackRequest) => paymentService.verifyPayment(request),
  })

  // Cancel order mutation
  const cancelOrderMutation = useMutation({
    mutationFn: (orderId: string) => paymentService.cancelOrder(orderId),
  })

  // Apply promo code
  const applyPromoCode = useCallback(async (code: string) => {
    const trimmed = code.trim()
    if (!trimmed) return

    setIsApplyingPromo(true)
    setPromoError(null)

    try {
      const response = await paymentService.applyPromoCode({ promoCode: trimmed })

      if (response.success && response.data.valid) {
        setPromoCode(trimmed)
        setPromoResult(response.data)
      } else {
        setPromoError(response.data?.message || response.message || 'Invalid promo code')
        setPromoResult(null)
        setPromoCode('')
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to apply promo code. Please try again.'
      setPromoError(message)
      setPromoResult(null)
      setPromoCode('')
    } finally {
      setIsApplyingPromo(false)
    }
  }, [])

  // Clear promo code
  const clearPromoCode = useCallback(() => {
    setPromoCode('')
    setPromoResult(null)
    setPromoError(null)
  }, [])

  // Open Razorpay checkout
  const openRazorpayCheckout = useCallback(
    (providerOrderId: string, amount: number, orderId: string, checkoutKey: string) => {
      const launchCheckout = () => {
        if (!window.Razorpay) {
          setPaymentError('Payment system is not available. Please refresh and try again.')
          setPaymentStatus('error')
          return
        }

        const options: RazorpayOptions = {
          key: checkoutKey,
          amount: amount * 100, // Razorpay expects amount in paise
          currency: PAYMENT_CONSTANTS.CURRENCY,
          name: PAYMENT_CONSTANTS.COMPANY_NAME,
          description: pricingData?.productName || 'Premium Subscription',
          order_id: providerOrderId,
          prefill: {
            name: userProfile?.fullName || userProfile?.firstName || '',
            email: userProfile?.email || '',
            contact: userProfile?.phoneNumber || '',
          },
          theme: {
            color: PAYMENT_CONSTANTS.THEME_COLOR,
          },
          handler: async (response: RazorpayResponse) => {
            // Payment successful, verify with backend
            setPaymentStatus('verifying')

            try {
              const verificationRequest: RazorpayCallbackRequest = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }

              const result = await verifyPaymentMutation.mutateAsync(verificationRequest)

              if (result.success && result.data.status === 'SUCCESS') {
                // Track payment success (GA4 + FB Pixel)
                trackPaymentSuccess(orderId, amount)
                fbTrackPurchase(orderId, amount)

                // Refresh user profile from server BEFORE showing success
                if (userProfile) {
                  try {
                    await refreshCurrentUser()
                  } catch (profileError) {
                    console.error('Failed to refresh profile after payment:', profileError)
                  }
                  queryClient.invalidateQueries({ queryKey: paymentKeys.subscription(userProfile.userId) })
                }

                // Set success AFTER profile is updated so UI reflects paid status
                // before the PaywallModal starts its close timer
                setPaymentStatus('success')
              } else if (result.data.status === 'PENDING') {
                // Payment is being processed
                setPaymentError('Payment is being processed. Please wait.')
                setPaymentStatus('error')
              } else {
                setPaymentError('Payment verification failed. Please contact support.')
                setPaymentStatus('error')
              }
            } catch (error) {
              console.error('Payment verification error:', error)
              setPaymentError('Payment verification failed. Please contact support.')
              setPaymentStatus('error')
            }
          },
          modal: {
            ondismiss: () => {
              // User closed the checkout modal
              trackPaymentAbandoned(orderId, 'checkout_modal_closed')
              cancelOrderMutation.mutate(orderId)
              setPaymentStatus('idle')
            },
            escape: true,
            animation: true,
          },
        }

        const razorpay = new window.Razorpay(options)
        razorpay.open()
        setPaymentStatus('checkout')
      }

      // Lazily inject the Razorpay script only when a payment is initiated
      if (window.Razorpay) {
        launchCheckout()
      } else {
        const script = document.createElement('script')
        script.src = 'https://checkout.razorpay.com/v1/checkout.js'
        script.onload = launchCheckout
        script.onerror = () => {
          setPaymentError('Failed to load payment system. Please check your connection and try again.')
          setPaymentStatus('error')
        }
        document.body.appendChild(script)
      }
    },
    [pricingData, userProfile, verifyPaymentMutation, cancelOrderMutation, queryClient]
  )

  // Initiate payment flow
  const initiatePayment = useCallback(async () => {
    if (!pricingData) {
      console.error('Payment error: Pricing data not available', { pricingData })
      setPaymentError('Pricing information not available. Please refresh and try again.')
      setPaymentStatus('error')
      return
    }

    if (!userProfile) {
      console.error('Payment error: User profile not available')
      setPaymentError('User profile not loaded. Please refresh and try again.')
      setPaymentStatus('error')
      return
    }

    setPaymentStatus('creating_order')
    setPaymentError(null)

    try {
      const orderRequest: InitiatePaymentRequest = {
        userId: userProfile.userId,
        paymentPurpose: 'SUBSCRIPTION',
        subscriptionTierCode: 'NEET_PREMIUM',
        ...(promoCode ? { promoCode } : {}),
        metadata: {
          customer_name: userProfile.fullName || userProfile.firstName || '',
          customer_email: userProfile.email || '',
          customer_phone: userProfile.phoneNumber || '',
        },
      }

      const orderResponse = await createOrderMutation.mutateAsync(orderRequest)

      if (!orderResponse.success || !orderResponse.data) {
        throw new Error(orderResponse.message || 'Failed to create order')
      }

      const { orderId, providerOrderId, amount, checkoutKey } = orderResponse.data

      // Track checkout started (GA4 + FB Pixel)
      trackCheckoutStarted(amount)
      fbTrackInitiateCheckout(amount)

      // Open Razorpay checkout
      openRazorpayCheckout(providerOrderId, amount, orderId, checkoutKey)
    } catch (error) {
      console.error('Order creation error:', error)
      setPaymentError('Failed to create order. Please try again.')
      setPaymentStatus('error')
    }
  }, [pricingData, userProfile, promoCode, createOrderMutation, openRazorpayCheckout])

  // Reset payment state
  const resetPayment = useCallback(() => {
    setPaymentStatus('idle')
    setPaymentError(null)
    setPromoCode('')
    setPromoResult(null)
    setPromoError(null)
  }, [])

  return {
    pricing: pricingData,
    isPricingLoading,
    pricingError: pricingError as Error | null,
    paymentStatus,
    paymentError,
    promoCode,
    promoResult,
    isApplyingPromo,
    promoError,
    initiatePayment,
    resetPayment,
    applyPromoCode,
    clearPromoCode,
  }
}
